// Configuration générale du jeu, réglée dans l'administration (onglet Configuration) : le nom donné aux
// créatures (« monstre » par défaut), celui de leur encyclopédie (« Monstredex ») et le cycle jour/nuit. Partagée par le serveur (messages), le jeu et l'administration.

import type { DayNightConfig } from './lighting.ts';
import type { ObstacleKind, Requirements } from './map-elements.ts';
import type { StatusCondition } from './moves.ts';

/** Nom des créatures, avec ce qu'il faut pour les accorder (« une créature sauvage », « cet oiseau »…). */
export interface CreatureNaming {
  singular: string;
  plural: string;
  feminine: boolean;
}

/** Nom de l'encyclopédie des créatures (le Pokédex), avec son genre (« le Monstredex », « la Bestiothèque »). */
export interface DexNaming {
  name: string;
  feminine: boolean;
}

/**
 * Capacités du joueur : couper un buisson, pousser un rocher, surfer sur l'eau (les éléments disent seulement ce qui
 * se coupe, se pousse ou se surfe), voler vers une ville déjà visitée (depuis la carte du monde).
 */
export type AbilityKind = ObstacleKind | 'fly';
export const ABILITY_KINDS: readonly AbilityKind[] = ['clear', 'push', 'surf', 'fly'];
export const ABILITY_LABELS: Readonly<Record<AbilityKind, string>> = { clear: 'Couper', push: 'Pousser', surf: 'Surfer', fly: 'Voler' };

/** Réglages d'une capacité : active ou non, l'action affichée et ses conditions (toutes doivent être remplies). */
export interface AbilityConfig extends Requirements {
  enabled: boolean;
  /** Action proposée au joueur (« Couper », « Surfer »…). */
  verb: string;
  /** Ce qui est dit quand il manque quelque chose (vide : la liste de ce qui manque). */
  blockedText: string;
}

/** Animations de combat qui ne viennent pas d'une attaque (identifiants de l'onglet Animations ; null : aucune). */
export interface BattleEffectsConfig {
  /** Jouée sur le monstre dont une statistique augmente, après l'attaque. */
  buffAnimationId: number | null;
  /** Jouée sur le monstre dont une statistique baisse, après l'attaque. */
  debuffAnimationId: number | null;
  /** Jouée en boucle sur le monstre tant qu'il a ce statut (une animation légère). */
  statusAnimations: Record<StatusCondition, number | null>;
}

/**
 * Sons de l'onglet Sons joués hors des réglages d'une carte (identifiants ; null : aucun) : la musique de chaque sorte
 * de combat (sans elle, celle de la carte continue), et l'ambiance ajoutée pendant la pluie, la neige ou le brouillard.
 */
export interface AudioConfig {
  wildBattleMusic: number | null;
  trainerBattleMusic: number | null;
  duelMusic: number | null;
  weather: Record<'rain' | 'snow' | 'fog', number | null>;
}

/** Multiplicateurs d'expérience proposés (onglet Configuration) : ×1 normal, au-delà un bonus (événement, test…). */
export const XP_BOOSTS = [1, 1.5, 2, 3, 5] as const;
export type XpBoost = (typeof XP_BOOSTS)[number];
export const isXpBoost = (value: unknown): value is XpBoost => (XP_BOOSTS as readonly unknown[]).includes(value);

export interface GameConfig {
  creature: CreatureNaming;
  dex: DexNaming;
  abilities: Record<AbilityKind, AbilityConfig>;
  dayNight: DayNightConfig;
  battle: BattleEffectsConfig;
  audio: AudioConfig;
  /** Multiplie l'expérience gagnée en combat (et en duel). */
  xpBoost: XpBoost;
  /** Part (en %) de l'expérience du vainqueur que reçoit chaque autre monstre de l'équipe, même K.O. ; 0 : aucun partage. */
  teamXpShare: number;
  /** Carte où arrivent les nouveaux joueurs (sur son point d'apparition) ; null ou carte supprimée : la plus ancienne. */
  startMapId: number | null;
  /**
   * Contrôle des déplacements par le serveur (vitesse, collisions). Coupé : le serveur croit le client sur parole (la case
   * où il dit être arrivé fait foi, sans limite de vitesse) et tolère les connexions très lentes ou instables.
   */
  antiCheat: boolean;
}

const ability = (verb: string, element: string | null = null): AbilityConfig => ({ enabled: true, verb, badgeId: null, element, item: null, blockedText: '' });

export const DEFAULT_GAME_CONFIG: GameConfig = {
  creature: { singular: 'monstre', plural: 'monstres', feminine: false },
  dex: { name: 'Monstredex', feminine: false },
  abilities: { clear: ability('Couper'), push: ability('Pousser'), surf: ability('Surfer', 'water'), fly: ability('Voler', 'air') },
  dayNight: { enabled: true, cycleMinutes: 24, nightDarkness: 0.65 },
  battle: { buffAnimationId: null, debuffAnimationId: null, statusAnimations: { poison: null, burn: null, paralysis: null, sleep: null, freeze: null } },
  audio: { wildBattleMusic: null, trainerBattleMusic: null, duelMusic: null, weather: { rain: null, snow: null, fog: null } },
  xpBoost: 1,
  teamXpShare: 15,
  startMapId: null,
  antiCheat: true,
};
export const GAME_CONFIG_LIMITS = { teamXpShareMax: 100, creatureName: 30, dexName: 30, abilityVerb: 20, abilityBlockedText: 300 } as const;

let current: GameConfig = DEFAULT_GAME_CONFIG;

/** Configuration en vigueur (celle du serveur une fois chargée). */
export const gameConfig = (): GameConfig => current;

export function setGameConfig(config: GameConfig): void {
  current = config;
}

/** Le nom commence par une voyelle (ou un h muet) : « l'ombre », « cet oiseau », « de l'hydre ». */
const elides = (word: string): boolean => /^[aeiouyhàâäéèêëîïôöûüù]/i.test(word);

/** Formes du nom de l'encyclopédie, selon le mot-clé d'un marqueur (en minuscules). */
function dexForm(key: string, { name, feminine }: DexNaming): string | null {
  const vowel = elides(name);
  switch (key) {
    case 'dex': return name;
    case 'le dex': return vowel ? `l'${name}` : `${feminine ? 'la' : 'le'} ${name}`;
    case 'du dex': return vowel ? `de l'${name}` : `${feminine ? 'de la' : 'du'} ${name}`;
    case 'au dex': return vowel ? `à l'${name}` : `${feminine ? 'à la' : 'au'} ${name}`;
    default: return null;
  }
}

/** Formes d'une créature, selon le mot-clé d'un marqueur (en minuscules). */
function creatureForm(key: string, { singular: one, plural: many, feminine }: CreatureNaming): string | null {
  const vowel = elides(one);
  switch (key) {
    case 'un': return feminine ? 'une' : 'un';
    case 'monstre': return one;
    case 'monstres': return many;
    case 'un monstre': return `${feminine ? 'une' : 'un'} ${one}`;
    case 'de monstres': return elides(many) ? `d'${many}` : `de ${many}`;
    case 'le monstre': return vowel ? `l'${one}` : `${feminine ? 'la' : 'le'} ${one}`;
    case 'ce monstre': return `${feminine ? 'cette' : vowel ? 'cet' : 'ce'} ${one}`;
    case 'du monstre': return vowel ? `de l'${one}` : `${feminine ? 'de la' : 'du'} ${one}`;
    case 'au monstre': return vowel ? `à l'${one}` : `${feminine ? 'à la' : 'au'} ${one}`;
    case 'mon monstre': return `${feminine && !vowel ? 'ma' : 'mon'} ${one}`;
    case 'ton monstre': return `${feminine && !vowel ? 'ta' : 'ton'} ${one}`;
    case 'son monstre': return `${feminine && !vowel ? 'sa' : 'son'} ${one}`;
    // Accords : pronoms et terminaison féminine d'un adjectif (« vaincu{·e} »).
    case 'il': return feminine ? 'elle' : 'il';
    case 'le': return feminine ? 'la' : 'le';
    case '·e': return feminine ? 'e' : '';
    default: return null;
  }
}

/** Marqueur : un mot-clé entre accolades (« {un monstre} », « {Monstres} », « {·e} »). */
const TOKEN = /\{([^{}\n]{1,16})\}/g;

/**
 * Remplace les marqueurs du nom des créatures dans un texte du jeu ou de l'administration : {monstre},
 * {monstres}, {un monstre}, {de monstres} (« d'amibits »), {le monstre}, {ce monstre}, {du monstre}, {au monstre}, {mon monstre},
 * {ton monstre}, {son monstre}, et pour les accords {un} (« {un} autre {monstre} »), {il}, {le} (pronom) et {·e}
 * (« vaincu{·e} »). « Auc{un monstre} » donne « Aucune créature ». Le nom de l'encyclopédie : {dex}, {le dex},
 * {du dex}, {au dex}. Une majuscule au début du marqueur en met une au résultat ; un marqueur inconnu (« {joueur} »)
 * est laissé tel quel.
 */
export function creatureText(text: string, config: GameConfig = current): string {
  return text.replace(TOKEN, (token, inner: string) => {
    const key = inner.toLowerCase();
    const form = dexForm(key, config.dex) ?? creatureForm(key, config.creature);
    if (form === null) return token;
    const first = inner.charAt(0);
    return first !== first.toLowerCase() ? form.charAt(0).toUpperCase() + form.slice(1) : form;
  });
}
